import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { signup as signupApi, getCounties } from '../api';
import FacilitySearchSelect from '../components/FacilitySearchSelect';

const WA_COUNTIES = [
  'Adams','Asotin','Benton','Chelan','Clallam','Clark','Columbia','Cowlitz',
  'Douglas','Ferry','Franklin','Garfield','Grant','Grays Harbor','Island',
  'Jefferson','King','Kitsap','Kittitas','Klickitat','Lewis','Lincoln','Mason',
  'Okanogan','Pacific','Pend Oreille','Pierce','San Juan','Skagit','Skamania',
  'Snohomish','Spokane','Stevens','Thurston','Wahkiakum','Walla Walla',
  'Whatcom','Whitman','Yakima',
];

function EyeIcon({ open }) {
  return open ? (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  ) : (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

export default function Signup() {
  const { login } = useAuth();
  const navigate  = useNavigate();

  const [form, setForm] = useState({
    first_name:        '',
    last_name:         '',
    email:             '',
    password:          '',
    organization_name: '',
    role_title:        '',
    primary_county:    '',
  });
  const [showPw, setShowPw]               = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [counties, setCounties] = useState(WA_COUNTIES);

  useEffect(() => {
    getCounties().then(setCounties).catch(() => {}); // fall back to hardcoded list
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const canSubmit =
    form.first_name.trim() &&
    form.last_name.trim() &&
    form.email.trim() &&
    form.password.length >= 8 &&
    agreedToTerms &&
    !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = {
        first_name:        form.first_name.trim(),
        last_name:         form.last_name.trim(),
        email:             form.email.trim(),
        password:          form.password,
        organization_name: form.organization_name.trim() || undefined,
        role_title:        form.role_title.trim() || undefined,
        primary_county:    form.primary_county || undefined,
        ...(selectedFacility ? { facility_id: selectedFacility.id } : {}),
      };
      const { user, token, refresh_token, session_id } = await signupApi(payload);
      login(user, token, refresh_token, session_id);
      // New accounts start as guest — send them to Subscribe page
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Signup failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Helmet>
        <title>Create Account | WA BHA Treatment Finder</title>
        <meta name="description" content="Create your WA BHA Treatment Finder account to access Washington State's behavioral health facility directory." />
      </Helmet>
      {/* Top bar */}
      <Header right={null} />

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 px-8 py-10">
            <h1 className="text-xl font-bold text-gray-900 mb-1">Create an account</h1>
            <p className="text-sm text-gray-500 mb-7">
              Create a free account for full facility details, filters, and bed availability data.
            </p>

            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              {/* Name row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    First name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.first_name}
                    onChange={set('first_name')}
                    autoComplete="given-name"
                    disabled={loading}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Last name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.last_name}
                    onChange={set('last_name')}
                    autoComplete="family-name"
                    disabled={loading}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  autoComplete="email"
                  placeholder="you@example.com"
                  disabled={loading}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={form.password}
                    onChange={set('password')}
                    autoComplete="new-password"
                    placeholder="8+ characters"
                    disabled={loading}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 pr-10 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <EyeIcon open={showPw} />
                  </button>
                </div>
                {form.password.length > 0 && form.password.length < 8 && (
                  <p className="text-xs text-red-500 mt-1">Must be at least 8 characters</p>
                )}
              </div>

              {/* Organization */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Organization / Agency <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={form.organization_name}
                  onChange={set('organization_name')}
                  autoComplete="organization"
                  placeholder="Agency or organization name"
                  disabled={loading}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {/* Facility link (optional) */}
              <div className="border border-gray-200 rounded-xl px-4 py-4 bg-gray-50">
                <p className="text-sm font-medium text-gray-700 mb-1">
                  Are you signing up on behalf of a listed facility?
                  <span className="text-gray-400 font-normal"> (optional)</span>
                </p>
                <p className="text-xs text-gray-400 mb-3">
                  Search for your agency to request a link. Once approved by admin, you can edit your listing.
                </p>
                <FacilitySearchSelect
                  selected={selectedFacility}
                  onSelect={setSelectedFacility}
                  disabled={loading}
                />
              </div>

              {/* Role + County row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Role / Title
                    <span className="text-gray-400 font-normal"> (optional)</span>
                  </label>
                  <input
                    type="text"
                    value={form.role_title}
                    onChange={set('role_title')}
                    placeholder="e.g. Case Manager"
                    disabled={loading}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Primary County
                    <span className="text-gray-400 font-normal"> (optional)</span>
                  </label>
                  <select
                    value={form.primary_county}
                    onChange={set('primary_county')}
                    disabled={loading}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="">Select…</option>
                    {counties.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                  <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z" />
                  </svg>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Terms agreement */}
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  disabled={loading}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <span className="text-sm text-gray-600">
                  I agree to the{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline font-medium"
                  >
                    Terms of Service and No Refund Policy
                  </a>
                </span>
              </label>

              {/* Submit */}
              <button
                type="submit"
                disabled={!canSubmit}
                className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-semibold text-sm transition-colors ${
                  canSubmit
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    Creating account…
                  </>
                ) : 'Create account'}
              </button>
            </form>
          </div>

          {/* Footer */}
          <p className="text-center text-sm text-gray-500 mt-5">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 font-medium hover:underline">
              Sign in
            </Link>
          </p>
          <p className="text-center text-sm text-gray-500 mt-2">
            Have a promo code?{' '}
            <Link to="/trial" className="text-blue-600 font-medium hover:underline">
              Redeem it here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
